import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAgentExecutionClient } from "src/agents/queries/execution";
import {
  type AgentInvokeResponse,
  type Data,
  type Environment,
  ExecutionMode,
  HumanMessageTypeEnum,
  type SessionMessagesResponse,
  type SessionMessagesResponseMessagesInner,
} from "src/clients/agent-management";
import { toastActions } from "src/design-system/Toast/utils";

type UseCaseCopilotInvokeParams = {
  agentId: string | undefined;
  agentVersionId: string | undefined;
  caseId: string;
  sessionId: string | null;
  environment: string;
  messagesQueryKey: readonly unknown[];
  onInvokeStarted: () => void;
};

export const useCaseCopilotInvoke = ({
  agentId,
  agentVersionId,
  caseId,
  sessionId,
  environment,
  messagesQueryKey,
  onInvokeStarted,
}: UseCaseCopilotInvokeParams) => {
  const client = useAgentExecutionClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      invocationMessage: string,
    ): Promise<AgentInvokeResponse> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      if (!agentId) {
        throw new Error("No copilot agent configured for this case template");
      }
      if (!sessionId) {
        throw new Error("Session ID not available - user ID is required");
      }

      // Cast needed: generated `Data` type merges all invoke variants,
      // making playground/flow-only fields incorrectly required.
      const data = {
        agent_id: agentId,
        agent_version_id: agentVersionId ?? "latest",
        case_id: caseId,
        session_id: sessionId,
        invocation_message: invocationMessage,
        type: "invoke",
        source: "case",
        control: {
          execution_mode: ExecutionMode.ASYNC,
          environment: environment as Environment,
        },
      } as unknown as Data;

      const response =
        await client.invokeAgentCopilotsManagementApiV0InvokePost({
          data,
        });
      return response.data;
    },
    onMutate: async (invocationMessage: string) => {
      await queryClient.cancelQueries({ queryKey: messagesQueryKey });

      const previousData =
        queryClient.getQueryData<SessionMessagesResponse>(messagesQueryKey);

      queryClient.setQueryData<SessionMessagesResponse>(
        messagesQueryKey,
        (old) =>
          ({
            // Clear terminal session status so isActive stays true
            // until the first real poll response arrives.
            session: old?.session
              ? { ...old.session, status: undefined }
              : old?.session,
            messages: [
              {
                type: HumanMessageTypeEnum.HUMAN,
                invocation_message: invocationMessage,
                content: invocationMessage,
              } as SessionMessagesResponseMessagesInner,
              ...(old?.messages ?? []),
            ],
          }) as SessionMessagesResponse,
      );

      return { previousData };
    },
    onSuccess: () => {
      onInvokeStarted();
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(messagesQueryKey, context.previousData);
      }
      toastActions.failure({
        title: "Failed to send message",
        description: "Please try again",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: messagesQueryKey });
    },
  });
};
